import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, type SupplierDetailData, type SupplierRowData } from '../api';
import { statusLabel, statusTone } from '../lib/labels';
import { Badge, Button, Card, ErrorMessage, Field, Input, Notice, PageHeader, Select, Spinner, Stat, Textarea, money, number, useConfirm } from '../ui';

export function SupplierDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const d = useQuery({ queryKey: ['supplier', id], queryFn: () => api<SupplierDetailData>(`/api/v1/suppliers/${id}`) });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['supplier', id] });
    void qc.invalidateQueries({ queryKey: ['suppliers'] });
  };
  if (d.isPending) return <Spinner />;
  if (d.error || !d.data) return <ErrorMessage error={d.error} />;
  const { supplier: s, stats, orders } = d.data;

  return (
    <>
      <PageHeader
        title={s.name}
        subtitle={
          <>
            <Link to="/suppliers" className="hover:underline">
              Suppliers
            </Link>
            {s.bricklinkUsername && (
              <>
                {' '}
                / BrickLink seller{' '}
                <a className="underline" href={`https://www.bricklink.com/store.asp?p=${encodeURIComponent(s.bricklinkUsername)}`} target="_blank" rel="noreferrer noopener">
                  {s.bricklinkUsername}
                </a>
              </>
            )}
          </>
        }
        actions={
          <>
            {!s.active && <Badge>Retired</Badge>}
            <Button variant="secondary" onClick={() => setEditing((v) => !v)}>
              {editing ? 'Cancel' : 'Edit'}
            </Button>
          </>
        }
      />
      <div className="space-y-4">
        {editing && <EditSupplier s={s} onDone={() => { setEditing(false); refresh(); }} />}

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Stat label="Orders" value={number(stats.orders)} note={stats.cancelled ? `${stats.cancelled} cancelled` : stats.firstOrder ? `since ${stats.firstOrder}` : 'None yet'} />
          <Stat label="Spent" value={money(stats.spent)} note="Postage included" />
          <Stat label="Postage" value={stats.postagePct === null ? '—' : `${stats.postagePct}%`} note="Of what the parts cost" />
          <Stat label="Days to arrive" value={stats.avgDaysToReceive === null ? '—' : stats.avgDaysToReceive} note="Order to receipt, on average" />
          <Stat label="Still in stock" value={number(stats.stockPieces)} note={stats.stockPieces ? `${money(stats.stockCost)} at cost` : 'Nothing left'} />
        </div>

        {(s.contact || s.notes || s.tags.length > 0) && (
          <Card title="About this supplier">
            <dl className="space-y-2 text-sm">
              {s.tags.length > 0 && <div className="flex flex-wrap gap-1.5">{s.tags.map((t) => <Badge key={t} tone="blue">{t}</Badge>)}</div>}
              {s.contact && (
                <div>
                  <dt className="text-xs text-slate-500">Contact</dt>
                  <dd className="whitespace-pre-wrap">{s.contact}</dd>
                </div>
              )}
              {s.notes && (
                <div>
                  <dt className="text-xs text-slate-500">Notes</dt>
                  <dd className="whitespace-pre-wrap">{s.notes}</dd>
                </div>
              )}
            </dl>
          </Card>
        )}

        <Card title="Orders and purchases">
          {orders.length === 0 ? (
            <p className="text-sm text-slate-500">Nothing bought from this supplier yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="data w-full min-w-[44rem] text-sm">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Order</th>
                    <th>Status</th>
                    <th className="text-right">Pieces</th>
                    <th className="text-right">Received</th>
                    <th className="text-right">Postage</th>
                    <th className="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="hover:bg-slate-50">
                      <td className="whitespace-nowrap">{o.purchasedOn}</td>
                      <td>
                        <Link to={`/purchases/${o.id}`} className="font-medium hover:underline">
                          {o.description || `Purchase ${o.id}`}
                        </Link>
                      </td>
                      <td>
                        <Badge tone={statusTone(o.status)}>{statusLabel(o.status)}</Badge>
                      </td>
                      <td className="text-right tabular-nums">{o.piecesOrdered === null ? <span className="text-slate-400">—</span> : number(o.piecesOrdered)}</td>
                      <td className="text-right tabular-nums">{o.piecesReceived === null ? <span className="text-slate-400">—</span> : number(o.piecesReceived)}</td>
                      <td className="text-right tabular-nums">{Number(o.shippingCost) > 0 ? money(o.shippingCost, o.currency) : <span className="text-slate-400">—</span>}</td>
                      <td className="text-right tabular-nums">{money(o.totalCost, o.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <MergeCard id={s.id} name={s.name} onDone={refresh} />
      </div>
    </>
  );
}

function EditSupplier({ s, onDone }: { s: SupplierDetailData['supplier']; onDone: () => void }) {
  const [name, setName] = useState(s.name);
  const [contact, setContact] = useState(s.contact ?? '');
  const [notes, setNotes] = useState(s.notes ?? '');
  const [tags, setTags] = useState(s.tags.join(', '));
  const [active, setActive] = useState(s.active);
  const save = useMutation({
    mutationFn: () =>
      api(`/api/v1/suppliers/${s.id}`, {
        method: 'PATCH',
        body: { name: name.trim(), contact: contact.trim() || null, notes: notes.trim() || null, tags: tags.split(',').map((t) => t.trim()).filter(Boolean), active },
      }),
    onSuccess: onDone,
  });
  return (
    <Card title="Edit supplier">
      <form
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Name">
          <Input required value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Tags" hint="Separated by commas, e.g. sale, closing down">
          <Input value={tags} onChange={(e) => setTags(e.target.value)} />
        </Field>
        <Field label="Contact" className="sm:col-span-2">
          <Textarea rows={2} value={contact} onChange={(e) => setContact(e.target.value)} />
        </Field>
        <Field label="Notes" className="sm:col-span-2">
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <label className="flex items-center gap-2 text-sm sm:col-span-2">
          <input type="checkbox" className="size-4 rounded border-slate-300" checked={!active} onChange={(e) => setActive(!e.target.checked)} />
          Retired (hidden from the list unless you ask for it; its orders stay)
        </label>
        <div className="sm:col-span-2">
          <Button type="submit" disabled={!name.trim() || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
      <div className="mt-3">
        <ErrorMessage error={save.error} />
      </div>
    </Card>
  );
}

/** For a store that appears twice, for instance after it changed its name or was keyed in by hand. */
function MergeCard({ id, name, onDone }: { id: number; name: string; onDone: () => void }) {
  const navigate = useNavigate();
  const { confirm, dialog } = useConfirm();
  const [into, setInto] = useState('');
  const others = useQuery({
    queryKey: ['suppliers', 'merge-targets'],
    queryFn: () => api<{ suppliers: SupplierRowData[] }>('/api/v1/suppliers?includeInactive=true').then((r) => r.suppliers),
  });
  const merge = useMutation({
    mutationFn: () => api<{ result: { purchasesMoved: number } }>(`/api/v1/suppliers/${id}/merge`, { body: { intoId: Number(into) } }),
    onSuccess: () => {
      onDone();
      navigate(`/suppliers/${into}`);
    },
  });
  const target = others.data?.find((o) => String(o.id) === into);
  return (
    <Card title="Same supplier twice?">
      {dialog}
      <p className="mb-3 text-sm text-slate-600">Fold this supplier into another one. Its orders move across and this record goes. Two different BrickLink sellers cannot be merged.</p>
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Merge into">
          <Select value={into} onChange={(e) => setInto(e.target.value)}>
            <option value="">Choose…</option>
            {others.data?.filter((o) => o.id !== id).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
                {o.bricklinkUsername ? ` (${o.bricklinkUsername})` : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Button
          variant="danger"
          disabled={!into || merge.isPending}
          onClick={async () => (await confirm(`Merge ${name} into ${target?.name}? This cannot be undone.`, { label: 'Merge', danger: true })) && merge.mutate()}
        >
          {merge.isPending ? 'Merging…' : 'Merge'}
        </Button>
      </div>
      <div className="mt-3">
        <ErrorMessage error={merge.error} />
      </div>
      {merge.isSuccess && <Notice tone="green">Merged.</Notice>}
    </Card>
  );
}
